/**
 * Упоминания в тексте сообщения (MSC3952).
 *
 * Element Desktop при ответе с упоминанием шлёт body вида «Антон: текст»
 * и formatted_body вида «<a href="matrix.to/#/@anton:server">Антон</a>: текст».
 * Наш клиент рисует упоминания как «@Имя» (с ссылкой на профиль), поэтому
 * «Имя:» в начале body нужно преобразовать в «@Имя» для отображения.
 * Серверный текст при этом не трогаем — только UI-рендер.
 */

/** Одно упоминание из m.mentions (как его отдаёт timeline.mentionsOf). */
export interface MentionEntry {
  userId: string;
  displayName: string;
  /** true — это упоминание самого себя: ссылкой в DM быть не должно. */
  self?: boolean;
}

/**
 * Извлечь из formatted_body текст ссылки-упоминания на userId
 * (например, «andrey (@andrey:algsuccess.com)») — имя НА МОМЕНТ ОТПРАВКИ.
 *
 * displayName из m.mentions — это ТЕКУЩЕЕ имя (room.getMember().name),
 * а в body/formatted_body лежит имя на момент отправки: переименования и
 * дизамбигуация («andrey (@andrey:…)») ломают матч по текущему имени.
 * Поэтому displayBody при наличии formatted_body берёт текст ссылки оттуда.
 *
 * Возвращает null, если ссылки на этот user id в html нет.
 */
function mentionNameFromHtml(html: string, userId: string): string | null {
  // Ищем <a href="https://matrix.to/#/<id>">ТЕКСТ</a> — матч по href после
  // декодирования (Element шлёт и «@user:server», и «%40user%3Aserver»).
  const re = /<a[^>]*href="(https:\/\/matrix\.to\/#\/[^"]+)"[^>]*>([^<]*)<\/a>/g;
  for (const match of html.matchAll(re)) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(match[1]);
    } catch {
      continue;
    }
    if (decoded === `https://matrix.to/#/${userId}`) {
      const text = match[2];
      // Текст ссылки может уже начинаться с «@» (наш клиент так шлёт) —
      // для сравнения с «Имя:» в plain body снимаем ведущий «@».
      return text.startsWith("@") ? text.slice(1) : text;
    }
  }
  return null;
}

/**
 * Преобразовать «Имя: текст» → «@Имя текст» для отображения.
 *
 * Срабатывает только если упоминание подтверждено сервером (есть в m.mentions)
 * и стоит в самом начале строки. Двоеточие после имени убираем: в нашем UI
 * упоминание — это @Имя, а не подпись автора.
 *
 * ИМЯ БЕРЁМ ИЗ formatted_body, если оно есть (параметр html): там имя
 * НА МОМЕНТ ОТПРАВКИ (текст ссылки), а displayName в m.mentions — ТЕКУЩЕЕ.
 * После переименования или при дизамбигуации («andrey (@andrey:…)») матч
 * по текущему имени не срабатывал, и «Имя:» оставалось в тексте
 * (дефект Антона 2026-10-09). Fallback — displayName, когда html нет
 * (превью без formatted_body и т.п.).
 *
 * @param body исходный текст сообщения (content.body)
 * @param mentions подтверждённые сервером упоминания (m.mentions)
 * @param html formatted_body, если есть — источник имени на момент отправки
 * @returns текст для отображения
 */
export function displayBody(
  body: string,
  mentions: MentionEntry[] | null,
  html?: string | null,
): string {
  if (!mentions || mentions.length === 0) return body;
  for (const m of mentions) {
    const name = (html ? mentionNameFromHtml(html, m.userId) : null) ?? m.displayName;
    const prefix = `${name}:`;
    if (body.startsWith(prefix)) {
      const rest = body.slice(prefix.length).trimStart();
      return `@${name}${rest ? ` ${rest}` : ""}`;
    }
  }
  return body;
}

/**
 * Преобразовать formatted_body: «<a href="matrix.to/#/@user:server">Имя</a>:»
 * → «<a href="matrix.to/#/@user:server">@Имя</a>».
 *
 * МАТЧ ПО ID, А НЕ ПО ИМЕНИ (дефект Антона 2026-10-09): ссылку находим по
 * href (декодированный user id ∈ m.mentions), а текст ссылки берём КАК ЕСТЬ —
 * там имя НА МОМЕНТ ОТПРАВКИ, которое может отличаться от текущего
 * displayName (переименования, дизамбигуация «andrey (@andrey:…)»).
 * Поэтому замена — «ТЕКСТ</a>:» → «@ТЕКСТ</a>», без подстановки displayName.
 *
 * Element Desktop шлёт href с НЕзакодированным user id
 * (https://matrix.to/#/@user:server), иногда весь абзац обёрнут в <p>…</p>.
 * Поэтому href сравниваем после декодирования, а перед ссылкой допускаем
 * необязательный открывающий <p> и пробельные символы.
 *
 * Работаем строкой, а не DOMParser: модуль общий для UI и уведомлений,
 * а в уведомлениях DOM недоступен.
 *
 * @param html исходный formatted_body
 * @param mentions подтверждённые сервером упоминания
 * @returns HTML для отображения
 */
export function displayFormattedBody(
  html: string,
  mentions: MentionEntry[] | null,
): string {
  if (!mentions || mentions.length === 0) return html;
  // Декодированные href → true для всех упомянутых user id.
  const ids = new Set(mentions.map((m) => `https://matrix.to/#/${m.userId}`));
  // Находим ссылки на matrix.to в начале строки (допускаем необязательный
  // <p> и пробелы перед ней), за которыми стоит двоеточие. Текст ссылки —
  // любой (без «<» внутри): матч по href, не по имени.
  const re =
    /^((?:<p>)?\s*)(<a[^>]*href="(https:\/\/matrix\.to\/#\/[^"]+)"[^>]*>)([^<]*)(<\/a>):/gm;
  return html.replace(
    re,
    (match, lead: string, openTag: string, href: string, text: string, closeTag: string) => {
      let decoded: string;
      try {
        decoded = decodeURIComponent(href);
      } catch {
        return match;
      }
      if (!ids.has(decoded)) return match;
      // Текст ссылки может уже начинаться с «@» (наш клиент так шлёт) —
      // не удваиваем.
      const shown = text.startsWith("@") ? text : `@${text}`;
      return `${lead}${openTag}${shown}${closeTag}`;
    },
  );
}
